import { compact } from "lodash";
import {
  AssessmentGroupDelivery,
  ComplianceProgramParticipantStatus,
  ComplianceProgramReportParticipantStatusFragment,
  ComplianceProgramReportParticipantStatusUserFragment,
  GetComplianceProgramProgressQuery,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";
import ManagedTable from "@components/table/managed-table";
import {
  UserComboboxDirectReportsOption,
  UserComboboxOptionType,
  UserComboboxOrgOption,
  UserComboboxReportingTreeOption,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";

import { useProgramsSection } from "../programs-section-context";
import {
  complianceProgramReportingTableColNameClassName,
  complianceProgramReportingTableColsClassName,
  getColumnDueDate,
} from "./compliance-program-progress-constants";
import ProgramProgressBar from "./program-progress-bar";
import ProgramProgressColumnHeader from "./program-progress-column-header";

export type ComplianceProgramReportingEntityTableParticipantStatusWithNominations =
  ComplianceProgramReportParticipantStatusFragment & {
    nominations: ComplianceProgramParticipantStatus;
  };

/**
 * The columns of the Progress tab: one per status a participant can hold.
 *
 * `steps`, `isOverdue` and `meeting` come on the same row but are not statuses, so they
 * are not columns. A step row names the column it belongs to with the same string.
 */
export type ComplianceProgramReportingEntityTableKeyName = keyof Omit<
  ComplianceProgramReportingEntityTableParticipantStatusWithNominations,
  "__typename" | "user" | "meeting" | "steps" | "isOverdue"
>;

export type ComplianceProgramReportingEntityTableCol = {
  label: string;
  keyName: ComplianceProgramReportingEntityTableKeyName;
  remindUsers?: ComplianceProgramReportParticipantStatusUserFragment[];
  remindUserTooltip?: string;
  remindUserType?:
    | "manager"
    | "subject"
    | "peer"
    | "directReport"
    | "survey_participant";
};

type ComplianceProgramReportingEntityTableStatusCount = {
  [key in ComplianceProgramParticipantStatus]: number;
};

const defaultStatusCount: ComplianceProgramReportingEntityTableStatusCount = {
  [ComplianceProgramParticipantStatus.NotApplicable]: 0,
  [ComplianceProgramParticipantStatus.NotStarted]: 0,
  [ComplianceProgramParticipantStatus.InProgress]: 0,
  [ComplianceProgramParticipantStatus.Complete]: 0,
};

type ComplianceProgramReportingTableEntity = (
  | UserComboboxOrgOption
  | UserComboboxTeamOption
  | UserComboboxDirectReportsOption
  | UserComboboxReportingTreeOption
) & {
  participantStatuses: ComplianceProgramReportingEntityTableParticipantStatusWithNominations[];
};

const ComplianceProgramProgressEntityTable = ({
  complianceProgram,
  entities,
  cols,
}: {
  complianceProgram: NonNullable<
    GetComplianceProgramProgressQuery["complianceProgram"]
  >;
  entities: ComplianceProgramReportingTableEntity[];
  cols: ComplianceProgramReportingEntityTableCol[];
}) => {
  const label = useLabel();
  const { basePath, noun } = useProgramsSection();
  const emptyStatusCountByCol = cols.reduce(
    (memo, col) => {
      return {
        ...memo,
        [col.keyName]: defaultStatusCount,
      };
    },
    {} as Record<
      ComplianceProgramReportingEntityTableKeyName,
      ComplianceProgramReportingEntityTableStatusCount
    >,
  );

  return entities.length === 0 ? (
    <div className="text-gray-500 text-sm">
      No {label("team")} members participating to this {noun.toLowerCase()}.
    </div>
  ) : (
    <ManagedTable
      scroll
      sortingEnabled
      sortDir="asc"
      sortKey="name"
      data={entities}
      className="overscroll-x-contain"
      columns={[
        {
          header: `Name`,
          headerClassName: complianceProgramReportingTableColNameClassName,
          cellClassName: complianceProgramReportingTableColNameClassName,
          render: (data: ComplianceProgramReportingTableEntity) => {
            return (
              <AppLink
                to={`${basePath}/${complianceProgram.id}/progress?${data.type}=${data.id}`}
                className="font-medium hover:underline"
              >
                {data.type === UserComboboxOptionType.TEAM
                  ? data.title
                  : "name" in data
                    ? data.name
                    : data.type === UserComboboxOptionType.DIRECT_REPORTS
                      ? "My Direct Reports"
                      : data.type === UserComboboxOptionType.REPORTING_TREE
                        ? "My Reporting Tree"
                        : ""}
              </AppLink>
            );
          },
          sortKey: "name",
          value: (data: ComplianceProgramReportingTableEntity) => {
            return data.type === UserComboboxOptionType.TEAM
              ? data.title
              : "name" in data
                ? data.name
                : data.type === UserComboboxOptionType.DIRECT_REPORTS
                  ? "My Direct Reports"
                  : data.type === UserComboboxOptionType.REPORTING_TREE
                    ? "My Reporting Tree"
                    : "";
          },
        },
        ...cols.map((col) => ({
          header: col.label,
          headerRender: () => (
            <ProgramProgressColumnHeader
              label={col.label}
              due={getColumnDueDate(
                entities.flatMap((entity) => entity.participantStatuses),
                col.keyName,
              )}
            />
          ),
          headerClassName: complianceProgramReportingTableColsClassName,
          cellClassName: complianceProgramReportingTableColsClassName,
          sortKey: col.keyName,
          value: (data: ComplianceProgramReportingTableEntity) => {
            const statusCountByCol = data.participantStatuses.reduce(
              (memo, participantStatus) => {
                return cols.reduce((memo2, col) => {
                  const currentValue = memo2[col.keyName];
                  return {
                    ...memo2,
                    [col.keyName]: {
                      ...currentValue,
                      [participantStatus[col.keyName]]:
                        memo2[col.keyName][participantStatus[col.keyName]] + 1,
                    },
                  };
                }, memo);
              },
              emptyStatusCountByCol as Record<
                ComplianceProgramReportingEntityTableKeyName,
                ComplianceProgramReportingEntityTableStatusCount
              >,
            );
            return (
              statusCountByCol[col.keyName][
                ComplianceProgramParticipantStatus.Complete
              ] *
                1000000000 +
              statusCountByCol[col.keyName][
                ComplianceProgramParticipantStatus.InProgress
              ] *
                1000000 +
              statusCountByCol[col.keyName][
                ComplianceProgramParticipantStatus.NotStarted
              ] *
                1000 +
              statusCountByCol[col.keyName][
                ComplianceProgramParticipantStatus.NotApplicable
              ]
            );
          },
          render: (data: ComplianceProgramReportingTableEntity) => {
            const statusCountByCol = data.participantStatuses.reduce(
              (memo, participantStatus) => {
                return cols.reduce((memo2, col) => {
                  const currentValue = memo2[col.keyName];
                  return {
                    ...memo2,
                    [col.keyName]: {
                      ...currentValue,
                      [participantStatus[col.keyName]]:
                        memo2[col.keyName][participantStatus[col.keyName]] + 1,
                    },
                  };
                }, memo);
              },
              emptyStatusCountByCol as Record<
                ComplianceProgramReportingEntityTableKeyName,
                ComplianceProgramReportingEntityTableStatusCount
              >,
            );
            return (
              <ProgramProgressBar
                className="w-full"
                notStarted={{
                  value:
                    statusCountByCol[col.keyName][
                      ComplianceProgramParticipantStatus.NotStarted
                    ],
                  label:
                    col.keyName === "oneononeStatus"
                      ? "Not Scheduled"
                      : col.keyName === "calibrationStatus"
                        ? "Not Calibrated"
                        : col.keyName === "deliveryStatus"
                          ? compact([
                              complianceProgram.performanceAssessmentTemplate,
                              complianceProgram.managerAssessmentTemplate,
                              complianceProgram.peerAssessmentTemplate,
                            ]).some(
                              (template) =>
                                template.delivery ===
                                AssessmentGroupDelivery.AdminApproval,
                            )
                            ? "Not Approved"
                            : "Not Shared"
                          : "Not Started",
                }}
                inProgress={{
                  value:
                    statusCountByCol[col.keyName][
                      ComplianceProgramParticipantStatus.InProgress
                    ],
                  label:
                    col.keyName === "oneononeStatus"
                      ? "Scheduled"
                      : col.keyName === "deliveryStatus"
                        ? "Approved"
                        : "In Progress",
                }}
                complete={{
                  value:
                    statusCountByCol[col.keyName][
                      ComplianceProgramParticipantStatus.Complete
                    ],
                  label:
                    col.keyName === "oneononeStatus"
                      ? "Finalized"
                      : col.keyName === "deliveryStatus"
                        ? "Shared"
                        : col.keyName === "calibrationStatus"
                          ? "Calibrated"
                          : "Complete",
                }}
              />
            );
          },
        })),
        {
          header: ``,
          render: () => "",
        },
      ]}
    />
  );
};

export default ComplianceProgramProgressEntityTable;
